/**
 * Docist TOC JavaScript
 * Handles smooth scrolling and active section highlighting for table of contents
 */

class TOCScrollSpy {
	constructor() {
		this.observer = null;
		this.headings = [];
		this.tocLinks = [];
		this.activeLink = null;
		this.isInitialized = false;
		this.lastActiveId = null;

		this.init();
	}

	init() {
		// Only initialize if Intersection Observer is supported
		if (!('IntersectionObserver' in window)) {
			console.warn('Docist TOC: Intersection Observer not supported, active highlighting disabled');
			return;
		}

		this.findElements();

		if (this.headings.length > 0 && this.tocLinks.length > 0) {
			this.setupObserver();
			this.isInitialized = true;
		}
	}

	findElements() {
		// Find all headings with IDs that could be in TOC
		this.headings = Array.from(document.querySelectorAll('h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]'));

		// Find all TOC links (since script only loads when global setting is enabled)
		this.tocLinks = Array.from(document.querySelectorAll('.docist-toc .docist-toc__link[href^="#"]'));
	}

	setupObserver() {
		const options = {
			rootMargin: '0px 0px -70% 0px', // Only upper 30% of viewport counts
			threshold: [0, 0.1],
		};

		this.observer = new IntersectionObserver((entries) => {
			this.handleIntersection(entries);
		}, options);

		this.headings.forEach((heading) => {
			this.observer.observe(heading);
		});
	}

	handleIntersection(entries) {
		const visible = entries.filter((entry) => entry.isIntersecting);

		let newActive = null;

		// First, try heading closest to top (positive offset)
		const topmost = visible
			.filter((entry) => entry.boundingClientRect.top >= 0)
			.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

		if (topmost.length > 0) {
			newActive = topmost[0].target;
		} else {
			// If none are below top, use one just above it
			const aboveTop = visible
				.filter((entry) => entry.boundingClientRect.top < 0)
				.sort((a, b) => b.boundingClientRect.top - a.boundingClientRect.top);

			if (aboveTop.length > 0) {
				newActive = aboveTop[0].target;
			}
		}

		if (newActive && newActive.id !== this.lastActiveId) {
			this.updateActiveLink(newActive.id);
			this.lastActiveId = newActive.id;
		}
	}

	updateActiveLink(headingId) {
		this.tocLinks.forEach((link) => {
			link.classList.remove('docist-toc__link--active');
		});

		const activeLink = this.tocLinks.find((link) => link.getAttribute('href') === `#${headingId}`);

		if (activeLink) {
			activeLink.classList.add('docist-toc__link--active');
			this.activeLink = activeLink;
		}
	}

	destroy() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
		this.isInitialized = false;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	// Initialize scroll spy (script only loads when global setting is enabled)
	const scrollSpy = new TOCScrollSpy();

	document.querySelectorAll('.docist-toc__list a[href^="#"]').forEach((link) => {
		link.addEventListener('click', (e) => {
			e.preventDefault();

			const targetId = link.getAttribute('href').slice(1);
			const targetEl = document.getElementById(targetId);

			if (targetEl) {
				const topOffset = 100; // Adjust based on your fixed header height
				const elementPosition = targetEl.getBoundingClientRect().top;
				const offsetPosition = elementPosition + window.pageYOffset - topOffset;

				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth',
				});

				// Manual fallback highlight in case observer doesn't trigger
				if (scrollSpy) {
					scrollSpy.updateActiveLink(targetId);
					scrollSpy.lastActiveId = targetId;
				}
			}
		});
	});
});
